import React from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner, alert } from 'equasion-lib';
import SalesOrders from './SalesOrders';
import { useRootStore } from '@/lib/providers/RootProvider';
import { companyUsesTotes } from '@/lib/totes';
import { companyUsesPickVerifyShip } from '@/lib/pickVerifyShip';
import {
  SET_MY_SHIPPING_LANE,
  WAREHOUSE_SHIPPING_LANES,
  type WarehouseShippingLane
} from '@/lib/shippingLane';

export const CLIENT_ORDERS = gql`
  query mobileSalesOrders($companyId: ID!) {
    mobileSalesOrders(companyId: $companyId) {
      id
      vendorSalesOrderNumber
      tote {
        name
      }
      # Bundled shipping — badge rows that have been set aside. vendorStatus stays 'shipping', so
      # this is the only way the list can tell a bundled order from a solo one.
      shippingBundle {
        id
        bundleNumber
        status
      }
      vendorStatus
      clientStatus
      createdAt
      updatedAt
      # Only to file each row under "partially picked" (see partiallyPicked below). This list holds
      # every order on the floor — hundreds at a large vendor — so select nothing per line that the
      # rows do not render: no product, no amounts.
      orderItems {
        id
        productId
        quantity
        serializedItemOfStatusCount(status: "PICKED")
        unserializedItemOfStatusCount(status: "PICKED")
      }
      isDestructionOrder
      client {
        id
        companyName
      }
      # Pick → Verify → Ship (Option 2): which tab an Option-2 order sits under, and who holds its
      # Pick claim. The workflow is 'standard' for every Option-1 order. pickerUserId as well as the
      # name: the name is null when the picker's user no longer resolves to one, and the claim must
      # still read as held ("Picking: another user", filed as in progress).
      fulfillmentWorkflow {
        workflow
        stage
        pickerUserId
        pickerName
      }
    }
    # Re-read per screen-open (network-only), not from the root store: it decides whether the list
    # has a Verify tab. The "id" is required so this normalizes to Company:<id>.
    company(id: $companyId) {
      id
      fulfillmentWorkflow
    }
  }
`;

function SalesOrdersData(props) {
  // `as any` on both reads: the root store is untyped, so this selector yields `{}`/`unknown`.
  const selectedCompany = useRootStore((state) => state.selectedCompany) as any;
  const companyId = selectedCompany?.id;
  // Root-store copy is fine here: this only decides whether a sub-label and a search clause render.
  // Anything that can BLOCK a picker reads the per-page copy instead (see @/lib/totes).
  const usesTotes = companyUsesTotes(selectedCompany as any);
  const { data, error, loading } = useQuery(CLIENT_ORDERS, {
    fetchPolicy: 'network-only',
    variables: { companyId }
  });
  // Separate from the list so a lane lookup failure leaves the list working (the select hides).
  const lanes = useQuery(WAREHOUSE_SHIPPING_LANES, {
    fetchPolicy: 'network-only',
    variables: { companyId },
    skip: !companyId
  });
  const [setMyShippingLane, { loading: savingLane }] = useMutation(SET_MY_SHIPPING_LANE);

  const changeShippingLane = async (lane: WarehouseShippingLane | null) => {
    try {
      await setMyShippingLane({
        variables: {
          companyId,
          locationId: lane?.locationId ?? null,
          laneNumber: lane?.number ?? null
        }
      });
      await lanes.refetch();
    } catch (e: any) {
      alert('Could not change shipping lane', e?.message || 'Try again.', 6, 'error');
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;

  const { mobileSalesOrders, company } = data || {};

  const salesOrdersWithPickingStatus = (mobileSalesOrders || []).map((order) => {
    const partiallyPicked = (order.orderItems || []).some(
      ({ serializedItemOfStatusCount, unserializedItemOfStatusCount }) =>
        serializedItemOfStatusCount > 0 || unserializedItemOfStatusCount > 0
    );
    return { ...order, partiallyPicked };
  });

  return (
    <SalesOrders
      salesOrders={salesOrdersWithPickingStatus}
      usesTotes={usesTotes}
      usesPickVerifyShip={companyUsesPickVerifyShip(company)}
      shippingLanes={lanes.data?.warehouseShippingLanes ?? []}
      myShippingLane={lanes.data?.myShippingLane ?? null}
      savingShippingLane={savingLane}
      onShippingLaneChange={changeShippingLane}
      {...props}
    />
  );
}

export default SalesOrdersData;
